if(!customElements.get('complete-set-component')){
  const COMPLETE_SET_LIST = 'Complete the Set';

  customElements.define(
    'complete-set-component',
    class CompleteSet extends HTMLElement {
        constructor() {
          super();
          this.initialized = false;
          
          this.state = {
            products: [],
            totals: { itemCount: 0, totalPrice: 0 },
            buttonText: 'ADD TO CART',
            initialCartState: [], // Array of variant IDs that were in cart on load
            hasAnyCompleteSetItemsInCart: false // Track if any complete set items exist in cart
          };
          
          // Event subscriptions
          this.cartUpdateUnsubscriber = null;
          
          // Loading state
          this.isLoading = false;
          
          this.cartSyncTimeout = null;
        }
      
        connectedCallback() {
          this.initializeCompleteSet();
    
          // Subscribe to cart updates for synchronization
          if (typeof subscribe === 'function') {
            this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
          }
        }
      
        async initializeCompleteSet() {
          this.initialized = true;

          const itemsContainer = this.querySelector('.add-ons-items');
          if (itemsContainer) {
            const itemRows = itemsContainer.querySelectorAll(':scope > .add-ons-item');
            if (itemRows.length === 0) return;

            this.state.products = Array.from(itemRows).map((row) => {
              const checkbox = row.querySelector('.add-ons-items__checkbox');
              const price = Number(checkbox?.dataset.product_price);
              return {
                id: checkbox?.dataset.product_id || '',
                sku: checkbox?.dataset.product_sku || '',
                vendor: checkbox?.dataset.product_vendor || '',
                type: checkbox?.dataset.product_type || '',
                title: checkbox?.dataset.product_title || '',
                price: Number.isFinite(price) ? price : 0,
                tags: checkbox?.dataset.product_tags || '',
                checked: false,
                inCart: false
              };
            }).filter((product) => product.id);

            if (this.state.products.length === 0) return;

            this.setupEventListeners();
            this.dispatchEvent(new CustomEvent('add-ons-content-updated', {
              bubbles: true
            }));

            const completeSetElement = document.querySelector('.complete-set');
            if (completeSetElement) {
              completeSetElement.style.display = 'flex';
            }
            this.toggleMobileImagesVisibility();
            await this.checkInitialCartState();
          }
        }
      
        setupEventListeners() {
          // Checkbox event listeners
          this.querySelectorAll('.add-ons-items__checkbox').forEach(checkbox => {
            checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
            // Prevent checkbox click from bubbling to the container
            checkbox.addEventListener('click', (e) => {
              e.stopPropagation();
            });
          });

          // Make entire .add-ons-item container clickable (desktop version)
          this.querySelectorAll('.add-ons-items .add-ons-item').forEach(item => {
            item.addEventListener('click', (e) => {
              // Don't trigger if clicking the link, button, or checkbox
              if (e.target.closest('.add-ons-items__img-link') || 
                  e.target.closest('.add-ons-item__cart-button') ||
                  e.target.closest('.add-ons-items__checkbox') ||
                  e.target.tagName === 'A') {
                return;
              }
              
              const checkbox = item.querySelector('.add-ons-items__checkbox');
              if (checkbox && !checkbox.disabled && !this.isLoading) {
                checkbox.checked = !checkbox.checked;
                checkbox.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
          });

          // Make mobile images clickable and sync with desktop checkboxes
          const mobileImagesContainer = this.querySelector('.add-ons-items--mobile__images');
          if (mobileImagesContainer) {
            mobileImagesContainer.querySelectorAll('.add-ons-item').forEach(mobileItem => {
              const link = mobileItem.querySelector('.add-ons-items__img-link');
              
              // Handle clicks on the mobile item to toggle checkbox
              mobileItem.addEventListener('click', (e) => {
                // Prevent link navigation - clicking toggles checkbox instead
                if (link) {
                  e.preventDefault();
                }
                
                const productId = mobileItem.dataset.product_id;
                if (!productId || this.isLoading) return;
                
                // Find corresponding desktop checkbox
                const desktopCheckbox = this.querySelector(`.add-ons-items__checkbox[data-product_id="${productId}"]`);
                if (desktopCheckbox && !desktopCheckbox.disabled) {
                  desktopCheckbox.checked = !desktopCheckbox.checked;
                  desktopCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
                }
              });
            });
          }

          // Button event listener
          const button = this.querySelector('.add-ons-item__cart-button');
          if (button) {
            button.addEventListener('click', this.handleButtonClick.bind(this));
          }
        }
    
        handleCheckboxChange(event) {
          const checkbox = event.target;
          
          // Prevent changes during loading or if checkbox is disabled
          if (this.isLoading || checkbox.disabled) {
            event.preventDefault();
            return;
          }
          
          const productId = checkbox.dataset.product_id;
          const product = this.state.products.find(p => p.id === productId);
          
          if (product) {
            product.checked = checkbox.checked;
            if (checkbox.checked) {
              window.ljList?.click(COMPLETE_SET_LIST, [this.listItem(product)], 'tick');
            }
            this.refreshUI();
          }
        }

        listItem(product) {
          return {
            item_id: product.sku || product.id,
            item_name: product.title,
            price: product.price / 100,
            item_brand: product.vendor || undefined,
            item_category: product.type || undefined
          };
        }

        trackListAdd(products) {
          if (!window.ljList || products.length === 0) return;
          window.ljList.click(COMPLETE_SET_LIST, products.map(p => this.listItem(p)), 'bundle_button');
          window.ljList.announceAdd(COMPLETE_SET_LIST, products.map(p => p.id));
        }
    
        async handleButtonClick(event) {
          event.preventDefault();
          
          // Prevent multiple clicks during loading
          if (this.isLoading) {
            return;
          }
          
          // Backup current state before operation
          const previousState = this.backupCurrentState();
          
          // IMMEDIATELY disable all checkboxes and button to prevent any user interaction
          this.setLoadingState(true);
          
          try {
            if (this.state.hasAnyCompleteSetItemsInCart) {
              await this.smartUpdateCart();
            } else {
              await this.addToCart();
            }
          } catch (error) {
            console.error('Cart operation failed:', error);
            // Silently revert to previous state instead of showing error
            this.restoreState(previousState);
          } finally {
            // Remove loading state - re-enable based on current state
            this.setLoadingState(false);
          }
        }
    
        moneyWithoutCurrency(price) {
          return (price / 100).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }
    
        async addToCart() {
          const checkedItems = this.getCheckedItems();
          const itemCount = checkedItems.length;
          
          if (checkedItems.length === 0) return;
          
          this.hideNotification();
          
          this.trackListAdd(this.state.products.filter(p => p.checked && !p.inCart));

          try {
            const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: checkedItems })
            });
            
        if (response.ok) {
          const cartData = await response.json();
          
          // Update state to match what's now in cart
          this.state.products.forEach(product => {
            product.inCart = product.checked;
          });
          
          // Update initial cart state and flags
          this.state.initialCartState = this.state.products
            .filter(p => p.inCart)
            .map(p => p.id);
          this.state.hasAnyCompleteSetItemsInCart = true;
              
              // Update UI without overriding checkbox states
              this.updateUI(false); // Don't override checkbox states
              
              // Show specific add message
              const message = itemCount === 1 ? '1 item added' : `${itemCount} items added to basket`;
              this.showNotification('add', itemCount, message);
              
              // Refresh cart drawer with our source identifier
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer && typeof cartDrawer.refresh === 'function') {
                cartDrawer.refresh(false, 'complete-set');
              }
            } else {
              throw new Error('Failed to add items to basket');
            }
          } catch (error) {
            console.error('Add to basket failed:', error);
          }
        }
    
      async smartUpdateCart() {
        const currentCheckedItems = this.state.products.filter(p => p.checked);
        const currentCartItems = this.state.products.filter(p => p.inCart);
        
        // Determine operations needed
        const itemsToAdd = currentCheckedItems.filter(p => !p.inCart);
        const itemsToRemove = currentCartItems.filter(p => !p.checked);
        
        if (itemsToAdd.length === 0 && itemsToRemove.length === 0) {
          return; // No changes needed
        }
        
        this.hideNotification();
        
        try {
          // Handle removals first if any
          if (itemsToRemove.length > 0) {
            // Fetch current cart to get quantities
            const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
            const cart = await cartResponse.json();
            
            const updates = {};
            itemsToRemove.forEach(item => {
              // Find current quantity in cart
              const cartItem = cart.items.find(ci => ci.variant_id.toString() === item.id);
              const currentQty = cartItem ? cartItem.quantity : 0;
              
              updates[item.id] = Math.max(0, currentQty - 1);
            });
            
            const removeResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates })
            });
            
            if (!removeResponse.ok) {
              throw new Error('Failed to remove items from cart');
            }
          }
          
          // Handle additions with properties if any
          if (itemsToAdd.length > 0) {
            // Build items array with delivery cost properties
            const itemsWithProperties = itemsToAdd.map(item => {
              // Get tags from checkbox or fallback to state
              const checkbox = this.querySelector(`[data-product_id="${item.id}"]`);
              const productTags = checkbox?.dataset.product_tags || item.tags;
              
              // Calculate delivery cost if delivery_tabs is loaded
              const lowestDeliveryCost = productTags && 
                typeof delivery_tabs !== 'undefined' && 
                delivery_tabs.courierData?.length > 0
                  ? delivery_tabs.calculateLowestDeliveryCost(productTags)
                  : null;
              
              const addItem = { id: item.id, quantity: 1, properties: { _list: COMPLETE_SET_LIST } };
              
              if (lowestDeliveryCost !== null) {
                addItem.properties['Lowest Delivery Cost'] = String(lowestDeliveryCost);
              }
              
              return addItem;
              });
          
            this.trackListAdd(itemsToAdd);

            const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: itemsWithProperties })
            });
            
            if (!addResponse.ok) {
              throw new Error('Failed to add items to cart');
            }
          }
          
            // Update internal state without fetching
            this.state.products.forEach(product => {
              product.inCart = product.checked;
            });
            
            // Update initial cart state
            this.state.initialCartState = this.state.products
              .filter(p => p.inCart)
              .map(p => p.id);
          
          // Update hasAnyCompleteSetItemsInCart flag
          this.state.hasAnyCompleteSetItemsInCart = this.state.initialCartState.length > 0;
          
          // Update UI without overriding checkbox states
          this.updateUI(false); // Don't override checkbox states
            
            // Generate detailed operation message
            const message = this.generateOperationMessage(itemsToAdd.length, itemsToRemove.length);
            this.showNotification('update', itemsToAdd.length + itemsToRemove.length, message);
            
            // Refresh cart drawer with our source identifier
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.refresh === 'function') {
              cartDrawer.refresh(false, 'complete-set');
            }
          } catch (error) {
            console.error('Smart update failed:', error);
          }
        }
    
        async checkInitialCartState() {
          try {
            const response = await fetch(`${window.Shopify.routes.root}cart.js`);
            const cart = await response.json();
            this.syncWithCart(cart, true); // true = initial load
          } catch (error) {
            console.error('Failed to check initial cart state:', error);
          }
        }
    
        handleCartUpdate(event) {
          // Only sync with cart if the update came from external sources
          if (event && event.source === 'complete-set') {
            return; // Ignore our own updates
          }
          
          if (this.cartSyncTimeout) {
            clearTimeout(this.cartSyncTimeout);
          }
          
          this.cartSyncTimeout = setTimeout(() => {
            fetch(`${window.Shopify.routes.root}cart.js`)
              .then(response => response.json())
              .then(cart => this.syncWithCart(cart, false)) // false = not initial load
              .catch(error => console.error('Cart sync failed:', error))
              .finally(() => {
                this.cartSyncTimeout = null;
              });
          }, 300);
        }
    
      syncWithCart(cart, isInitialLoad = false) {
        const cartVariantIds = new Set(
          cart.items
            .filter(item => item.quantity > 0)
            .map(item => item.variant_id.toString())
        );
        
        this.state.initialCartState = this.state.products
          .filter(p => cartVariantIds.has(p.id))
          .map(p => p.id);
        
        // If no items are in cart, select all checkboxes
        const hasNoItemsInCart = this.state.initialCartState.length === 0;
        
        this.state.products.forEach(product => {
          product.inCart = cartVariantIds.has(product.id);
          
          // Only force checkbox state on initial load
          if (isInitialLoad) {
            product.checked = hasNoItemsInCart ? true : product.inCart;
          }
        });
        
        this.state.hasAnyCompleteSetItemsInCart = this.state.initialCartState.length > 0;
        
        this.updateUI(!isInitialLoad ? false : true);
      }
      
        // Change detection logic - key for button state
        hasSelectionChanged() {
          const currentCheckedIds = this.state.products
            .filter(p => p.checked)
            .map(p => p.id)
            .sort();
          
          const currentCartIds = this.state.products
            .filter(p => p.inCart)
            .map(p => p.id)
            .sort();
          
          if (currentCheckedIds.length !== currentCartIds.length) return true;
          
          return !currentCheckedIds.every((id, index) => id === currentCartIds[index]);
        }
    
        getCheckedItems() {
          return this.state.products
            .filter(p => p.checked && !p.inCart)
            .map(p => {
              // Get tags from checkbox or fallback to state
              const checkbox = this.querySelector(`[data-product_id="${p.id}"]`);
              const productTags = checkbox?.dataset.product_tags || p.tags;
              
              // Calculate delivery cost
              const lowestDeliveryCost = productTags && 
                typeof delivery_tabs !== 'undefined' && 
                delivery_tabs.courierData?.length > 0
                  ? delivery_tabs.calculateLowestDeliveryCost(productTags)
                  : null;
              
              const item = { id: p.id, quantity: 1, properties: { _list: COMPLETE_SET_LIST } };
              
              if (lowestDeliveryCost !== null) {
                item.properties['Lowest Delivery Cost'] = String(lowestDeliveryCost);
              }
              
              return item;
            });
        }
    
        backupCurrentState() {
          // Create a deep copy of current state for restoration if operation fails
          return {
            products: this.state.products.map(product => ({
              id: product.id,
              checked: product.checked,
              inCart: product.inCart
            })),
            initialCartState: [...this.state.initialCartState],
            hasAnyCompleteSetItemsInCart: this.state.hasAnyCompleteSetItemsInCart,
            totals: { ...this.state.totals },
            buttonText: this.state.buttonText
          };
        }
    
        restoreState(previousState) {
          // Restore the previous state silently
          this.state.products = previousState.products;
          this.state.initialCartState = previousState.initialCartState;
          this.state.hasAnyCompleteSetItemsInCart = previousState.hasAnyCompleteSetItemsInCart;
          this.state.totals = previousState.totals;
          this.state.buttonText = previousState.buttonText;
          
          // Update UI to reflect restored state
          this.updateUI();
        }
    
        updateTotals() {
          const checkedProducts = this.state.products.filter(p => p.checked);
          this.state.totals.itemCount = checkedProducts.length;
          this.state.totals.totalPrice = checkedProducts.reduce((sum, p) => sum + p.price, 0);
          
          // Update DOM elements
          const totalElement = this.querySelector('.add-ons-item__total');
          const priceElement = this.querySelectorAll('.add-ons-item__total-price');
          
          if (totalElement) {
            totalElement.textContent = `Total (${this.state.totals.itemCount} Item/s)`;
          }
          
          if (priceElement.length > 0) {
            priceElement.forEach(element => {
              element.textContent = formatMoney(this.moneyWithoutCurrency(this.state.totals.totalPrice));
            });
          }
        }
    
        updateButtonState() {
          const button = this.querySelector('.add-ons-item__cart-button');
          if (!button) return;
          
          const buttonState = this.getButtonState();
          this.state.buttonText = buttonState.text;
          button.disabled = buttonState.disabled;
          
          const buttonText = button.querySelector('span');
          if (buttonText) {
            buttonText.textContent = this.state.buttonText;
          }
        }
    
        getButtonState() {
          const button = this.querySelector('.add-ons-item__cart-button');
          const dynamicButtonText = button?.dataset?.buttonText || 'ADD TO BASKET';
          
          const hasChecked = this.state.products.some(p => p.checked);
          const hasInCart = this.state.products.some(p => p.inCart);
          const hasChanges = this.hasSelectionChanged();
          
          // No items checked, no items in cart
          if (!hasChecked && !hasInCart) {
            return { text: dynamicButtonText, disabled: true };
          }
          
          // No items checked, but items in cart (remove all scenario)
          if (!hasChecked && hasInCart) {
            return { text: 'UPDATE ITEMS IN BASKET', disabled: false };
          }
          
          // Items checked, no items in cart (fresh add)
          if (hasChecked && !this.state.hasAnyCompleteSetItemsInCart) {
            return { text: dynamicButtonText, disabled: false };
          }
          
          // Items checked, items in cart (update scenario)
          return { text: 'UPDATE ITEMS IN BASKET', disabled: !hasChanges };
        }
    
        generateOperationMessage(addedCount, removedCount) {
          const messages = [];
          
          if (addedCount > 0) {
            messages.push(addedCount === 1 ? '1 item added' : `${addedCount} items added`);
          }
          
          if (removedCount > 0) {
            messages.push(removedCount === 1 ? '1 item removed' : `${removedCount} items removed`);
          }
          
          return messages.join(', ');
        }
    
        refreshUI() {
          // Quick update for totals and button state only (used during interactions)
          this.updateTotals();
          this.updateButtonState();
          this.toggleMobileImagesVisibility();
        }
        
        toggleMobileImagesVisibility() {
          // Toggle visibility of mobile images based on checkbox state
          const mobileImagesContainer = this.querySelector('.add-ons-items--mobile__images');
          if (!mobileImagesContainer) return;
          
          mobileImagesContainer.querySelectorAll('.add-ons-item').forEach(mobileItem => {
            const productId = mobileItem.dataset.product_id;
            if (!productId) return;
            
            // Find corresponding checkbox
            const checkbox = this.querySelector(`.add-ons-items__checkbox[data-product_id="${productId}"]`);
            if (checkbox) {
              if (checkbox.checked) {
                mobileItem.classList.remove('add-ons-item--hidden');
                mobileItem.classList.add('add-ons-item--visible');
              } else {
                mobileItem.classList.remove('add-ons-item--visible');
                mobileItem.classList.add('add-ons-item--hidden');
              }
            }
          });
        }
    
        updateUI(syncCheckboxes = true) {
          // Full UI update including checkboxes (used after cart operations)
          this.querySelectorAll('.add-ons-items__checkbox').forEach(checkbox => {
            const productId = checkbox.dataset.product_id;
            const product = this.state.products.find(p => p.id === productId);
            
            if (product) {
              // Only sync checkbox state if explicitly requested (not when coming out of loading)
              if (syncCheckboxes) {
                checkbox.checked = product.checked;
              }
              // Checkboxes stay enabled unless in loading state
              if (!this.isLoading) {
                checkbox.disabled = false;
              }
            }
          });

          this.refreshUI();
        }
    
        setLoadingState(loading) {
          this.isLoading = loading;
          
          const button = this.querySelector('.add-ons-item__cart-button');
          const spinner = button?.querySelector('.loading__spinner');
          const checkboxes = this.querySelectorAll('.add-ons-items__checkbox');
          
          if (loading) {
            // Disable button and show spinner
            if (button) {
              button.disabled = true;
              button.setAttribute('aria-busy', 'true');
            }
            if (spinner) {
              spinner.classList.remove('hidden'); // Show spinner by removing hidden class
            }
            
            // Disable all checkboxes during operation
            checkboxes.forEach(checkbox => {
              checkbox.disabled = true;
              checkbox.setAttribute('aria-busy', 'true');
            });
            
            // Add loading class to component for styling
            this.classList.add('loading');
            
          } else {
            // Re-enable based on current state
            if (button) {
              button.removeAttribute('aria-busy');
              // Button state will be set by updateButtonState()
            }
            if (spinner) {
              spinner.classList.add('hidden'); // Hide spinner by adding hidden class
            }
            
            // Re-enable all checkboxes
            checkboxes.forEach(checkbox => {
              checkbox.disabled = false;
              checkbox.removeAttribute('aria-busy');
            });
            
            // Remove loading class
            this.classList.remove('loading');
            
            // Update UI but don't override user checkbox changes
            this.updateUI(false);
          }
        }

        showNotification(operation, itemCount = 1, customMessage = null) {
  
          //scroll to top of the addons items when showing notification
          const addonsItemsContainer = this.querySelector('.add-ons-items');
          if (addonsItemsContainer) {
            addonsItemsContainer.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }
        
          const notification = this.querySelector('.add-ons-items__notification');
          const notificationContent = this.querySelector('.add-ons-items__notification-content');
          const notificationText = notificationContent?.querySelector('p');
          
          if (!notification || !notificationContent || !notificationText) return;
          
          // Set notification text based on operation
          if (operation === 'add') {
            notificationText.textContent = customMessage || (itemCount === 1 ? '1 item added' : 'Items added to cart');
          } else if (operation === 'update') {
            notificationText.textContent = customMessage || 'Updated items in cart';
          }
          
          // Show notification with animation
          notification.style.display = 'flex';
          
          // Force reflow to ensure display change is applied before animation
          notification.offsetHeight;
          
          // Trigger fade in and slide animation
          notification.style.opacity = '1';
          notification.style.transform = 'translateY(0)';
        
          // Hide notification after 2 seconds
          setTimeout(() => {
            this.hideNotification();
          }, 2100);
        }
        
        hideNotification() {
          const notification = this.querySelector('.add-ons-items__notification');
          if (!notification) return;
          
          // Start fade out and slide up animation
          notification.style.opacity = '0';
          
          // Wait for animation to complete before hiding
          setTimeout(() => {
            notification.style.display = 'none';
          notification.style.transform = 'translateY(20px)';
        }, 300); // Match this to your CSS transition duration
        }
    
    
        disconnectedCallback() {
            if (this.cartUpdateUnsubscriber) {
              this.cartUpdateUnsubscriber();
            }
            
            if (this.boundResizeHandler) {
                window.removeEventListener('resize', this.boundResizeHandler);
            }
            
            // Clear any pending cart sync timeout
            if (this.cartSyncTimeout) {
              clearTimeout(this.cartSyncTimeout);
            }
        }
      
    }
  )
}